import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import SAR from '../../../components/SAR';
import { useToast } from '../../context/ToastContext';
import { useDialog } from '../../../context/DialogContext';

const WS_BASE = process.env.NODE_ENV === 'production'
  ? `wss://${window.location.host}/api/v1`
  : 'ws://localhost:8002/api/v1';

const STATUS_MAP = {
  pending:             { label: 'في الانتظار',         cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  confirmed:           { label: 'مؤكد',               cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  no_driver_available: { label: 'جارٍ البحث عن سائق', cls: 'bg-orange-100 text-orange-700 border-orange-200' },
  assigned:            { label: 'تم تعيين سائق',       cls: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  accepted:            { label: 'السائق في الطريق',    cls: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
  started:             { label: 'جارٍ التوصيل',        cls: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
  en_route_pickup:     { label: 'السائق في الطريق',    cls: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
  at_source:           { label: 'السائق عند المصدر',   cls: 'bg-purple-100 text-purple-700 border-purple-200' },
  loading:             { label: 'جارٍ التعبئة',        cls: 'bg-purple-100 text-purple-700 border-purple-200' },
  en_route_delivery:   { label: 'في طريقه إليك',       cls: 'bg-teal-100 text-teal-700 border-teal-200' },
  arrived:             { label: 'وصل السائق',          cls: 'bg-teal-100 text-teal-700 border-teal-200' },
  delivering:          { label: 'جارٍ التسليم',        cls: 'bg-green-100 text-green-700 border-green-200' },
  completed:           { label: 'مكتمل',               cls: 'bg-green-100 text-green-700 border-green-200' },
  cancelled:           { label: 'ملغى',                cls: 'bg-red-100 text-red-700 border-red-200' },
  collecting_offers:   { label: 'جمع العروض',          cls: 'bg-sky-100 text-sky-700 border-sky-200' },
  offers_ready:        { label: 'العروض جاهزة',        cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  expired_no_offers:   { label: 'انتهت مهلة العروض',  cls: 'bg-orange-100 text-orange-700 border-orange-200' },
};

const ACTIVE_STATUSES = new Set([
  'pending','confirmed','no_driver_available','assigned','accepted',
  'started','en_route_pickup','at_source','loading',
  'en_route_delivery','arrived','delivering',
]);

function matchTab(order, tab) {
  if (tab === '')          return true;
  if (tab === 'active')    return ACTIVE_STATUSES.has(order.status);
  if (tab === 'completed') return order.status === 'completed';
  if (tab === 'cancelled') return order.status === 'cancelled';
  if (tab === 'pending')   return order.status === 'pending';
  return order.status === tab;
}

export default function OrdersPage() {
  const { apiCall, customer, getToken } = useCustomerAuth();
  const navigate    = useNavigate();
  const toast       = useToast();
  const { confirm: dialog } = useDialog();
  const [allOrders, setAllOrders] = useState([]);
  const [tab,       setTab]       = useState('');
  const [loading,   setLoading]   = useState(true);
  const wsRef = useRef(null);

  const cancelOrder = useCallback(async (e, orderId) => {
    e.preventDefault(); e.stopPropagation();
    if (!await dialog({ title: 'إلغاء الطلب', message: 'هل أنت متأكد من إلغاء هذا الطلب؟', type: 'warning', confirmText: 'إلغاء الطلب', cancelText: 'تراجع' })) return;
    try {
      await apiCall(`/customer/orders/${orderId}/cancel`, { method: 'POST' });
      toast.success('تم إلغاء الطلب');
      setAllOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: 'cancelled' } : o));
    } catch (err) { toast.error(err.message || 'تعذّر الإلغاء'); }
  }, [apiCall, dialog, toast]); // eslint-disable-line

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await apiCall('/customer/orders?limit=100');
        setAllOrders(data.orders || []);
      } catch { /* silent */ }
      finally { setLoading(false); }
    };
    load();
  }, [apiCall]);

  // ── Real-time WebSocket ──
  useEffect(() => {
    if (!customer?.id) return;
    const token = getToken();
    const ws = new WebSocket(`${WS_BASE}/ws/customer/${customer.id}?token=${token || ''}`);
    wsRef.current = ws;

    const ping = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) ws.send('ping');
    }, 25000);

    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.type !== 'order_status') return;
        const { order_id, status } = msg;
        setAllOrders(prev =>
          prev.map(o => o.id === order_id ? { ...o, status } : o)
        );
      } catch { /* ignore */ }
    };

    ws.onerror = () => {};

    return () => {
      clearInterval(ping);
      ws.close();
    };
  }, [customer?.id, getToken]);

  const orders = allOrders.filter(o => matchTab(o, tab));
  const count  = (t) => allOrders.filter(o => matchTab(o, t)).length;
  const fmtDate = d => d ? new Date(d).toLocaleDateString('ar-SA', { month:'short', day:'numeric', year:'numeric' }) : '—';

  const TABS = [
    { value: '',          label: 'الكل',        color: null },
    { value: 'active',    label: 'نشطة',        color: '#22d3ee' },
    { value: 'pending',   label: 'في الانتظار', color: '#fcd34d' },
    { value: 'completed', label: 'مكتملة',      color: '#6ee7b7' },
    { value: 'cancelled', label: 'ملغية',       color: '#fda4af' },
  ];

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'1rem', fontFamily:"'Cairo',sans-serif" }}>
      <style>{`
        @keyframes wt-live-dot { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.4;transform:scale(0.75)} }
        @keyframes wt-live-ring { 0%{transform:scale(1);opacity:0.6} 100%{transform:scale(2.2);opacity:0} }
        @keyframes wt-card-in   { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        .wt-ord-card {
          display:flex; flex-direction:column;
          border-radius:1rem; text-decoration:none;
          background:var(--wt-surface);
          border:1px solid var(--wt-border);
          transition:transform 0.18s ease, box-shadow 0.18s ease;
          overflow:hidden;
          animation: wt-card-in 0.3s ease both;
        }
        .wt-ord-card:active { transform:scale(0.985); }
        .wt-ord-card:hover  { box-shadow:0 6px 28px rgba(10,186,181,0.14); border-color:rgba(10,186,181,0.28); }
        .wt-tab-pill {
          flex-shrink:0; display:flex; align-items:center; gap:0.3rem;
          padding:0.42rem 0.85rem; border-radius:999px;
          font-size:0.77rem; font-weight:700; cursor:pointer;
          transition:all 0.18s; border:1px solid transparent;
          font-family:"'Cairo',sans-serif";
        }
      `}</style>

      {/* ── Header ── */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div>
          <h1 style={{ color:'var(--wt-text-primary)', fontSize:'1.3rem', fontWeight:900, margin:0, letterSpacing:'-0.02em' }}>
            طلباتي
          </h1>
          <p style={{ color:'var(--wt-text-muted)', fontSize:'0.72rem', margin:'0.1rem 0 0', display:'flex', alignItems:'center', gap:'0.3rem' }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
            </svg>
            {allOrders.length} طلب إجمالاً
          </p>
        </div>
        <button
          onClick={() => navigate('/customer/orders/new')}
          style={{
            display:'flex', alignItems:'center', gap:'0.4rem',
            padding:'0.6rem 1.1rem', borderRadius:'0.875rem', border:'none',
            background:'linear-gradient(135deg,#0ABAB5,#0891b2)',
            color:'#fff', fontSize:'0.82rem', fontWeight:800,
            cursor:'pointer', boxShadow:'0 4px 18px rgba(10,186,181,0.32)',
            fontFamily:"'Cairo',sans-serif",
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M12 4v16M4 12h16"/>
          </svg>
          طلب جديد
        </button>
      </div>

      {/* ── Stats strip ── */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'0.5rem' }}>
        {[
          { label:'نشطة',    val: count('active'),    color:'#0ABAB5', icon:'M12 2C9 5.5 5 9.3 5 13.5a7 7 0 0014 0C19 9.3 15 5.5 12 2z' },
          { label:'انتظار',  val: count('pending'),   color:'#f59e0b', icon:'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z' },
          { label:'مكتملة',  val: count('completed'), color:'#10b981', icon:'M22 11.08V12a10 10 0 11-5.93-9.14' },
          { label:'ملغية',   val: count('cancelled'), color:'#f43f5e', icon:'M18 6L6 18M6 6l12 12' },
        ].map(s => (
          <div key={s.label} style={{
            borderRadius:'0.875rem', padding:'0.6rem 0.5rem', textAlign:'center',
            background:`rgba(${hexToRgb(s.color)},0.08)`,
            border:`1px solid rgba(${hexToRgb(s.color)},0.2)`,
          }}>
            <div style={{ fontSize:'1.15rem', fontWeight:900, color:s.color, lineHeight:1 }}>{s.val}</div>
            <div style={{ fontSize:'0.62rem', fontWeight:700, color:`rgba(${hexToRgb(s.color)},0.8)`, marginTop:'0.15rem' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── Filter Tabs ── */}
      <div style={{ display:'flex', gap:'0.4rem', overflowX:'auto', paddingBottom:'0.2rem' }}>
        {TABS.map(t => {
          const n = count(t.value);
          const active = tab === t.value;
          const rgb = t.color ? hexToRgb(t.color) : '10,186,181';
          const col = t.color || '#0ABAB5';
          return (
            <button key={t.value} onClick={() => setTab(t.value)}
              className="wt-tab-pill"
              style={{
                background: active ? `rgba(${rgb},0.14)` : 'var(--wt-surface)',
                border: `1px solid ${active ? col : 'var(--wt-border)'}`,
                color: active ? col : 'var(--wt-text-muted)',
                boxShadow: active ? `0 0 14px rgba(${rgb},0.2)` : 'none',
              }}
            >
              {t.label}
              {n > 0 && (
                <span style={{
                  background: active ? `rgba(${rgb},0.18)` : 'transparent',
                  color: active ? col : 'var(--wt-text-muted)',
                  fontSize:'0.62rem', fontWeight:800,
                  borderRadius:999, padding:'0.05rem 0.4rem',
                  border: active ? `1px solid rgba(${rgb},0.25)` : 'none',
                }}>{n}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── List ── */}
      {loading ? (
        <div style={{ display:'flex', flexDirection:'column', gap:'0.6rem' }}>
          {[1,2,3].map(i => (
            <div key={i} className="wt-skeleton" style={{ height:96, borderRadius:'1rem' }}/>
          ))}
        </div>
      ) : orders.length === 0 ? (
        <div style={{
          padding:'3rem 1.5rem', textAlign:'center', borderRadius:'1.25rem',
          background:'var(--wt-surface)', border:'1px solid var(--wt-border)',
        }}>
          <div style={{
            width:64, height:64, borderRadius:'50%', margin:'0 auto 1rem',
            background:'rgba(10,186,181,0.08)', border:'1px solid rgba(10,186,181,0.18)',
            display:'flex', alignItems:'center', justifyContent:'center',
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="rgba(10,186,181,0.5)">
              <path d="M12 2C9 5.5 5 9.3 5 13.5a7 7 0 0014 0C19 9.3 15 5.5 12 2z"/>
            </svg>
          </div>
          <p style={{ color:'var(--wt-text-primary)', fontWeight:700, margin:'0 0 0.3rem' }}>لا توجد طلبات</p>
          <p style={{ color:'var(--wt-text-muted)', fontSize:'0.8rem', margin:'0 0 1.25rem' }}>ابدأ بطلب مياهك الآن</p>
          <button onClick={() => navigate('/customer/orders/new')} style={{
            padding:'0.65rem 1.75rem', borderRadius:'0.875rem', border:'none',
            background:'linear-gradient(135deg,#0ABAB5,#0891b2)', color:'#fff',
            fontSize:'0.85rem', fontWeight:800, cursor:'pointer',
            fontFamily:"'Cairo',sans-serif", boxShadow:'0 4px 16px rgba(10,186,181,0.3)',
          }}>
            اطلب الآن
          </button>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:'0.55rem' }}>
          {orders.map((order, idx) => {
            const st        = STATUS_MAP[order.status] || { label: order.status };
            const isAct     = ACTIVE_STATUSES.has(order.status);
            const isDone    = order.status === 'completed';
            const isCan     = order.status === 'cancelled';
            const canCancel = ['pending','confirmed','no_driver_available','collecting_offers','expired_no_offers'].includes(order.status);
            const amt    = order.actual_total || order.estimated_total || 0;

            const accentColor = isAct ? '#0ABAB5' : isDone ? '#10b981' : isCan ? '#f43f5e' : '#f59e0b';
            const accentRgb   = isAct ? '10,186,181' : isDone ? '16,185,129' : isCan ? '244,63,94' : '245,158,11';

            return (
              <Link key={order.id} to={`/customer/orders/${order.id}`}
                className="wt-ord-card"
                style={{ animationDelay:`${idx*0.04}s` }}
              >
                {/* Accent top bar */}
                <div style={{ height:2.5, background:`linear-gradient(90deg,${accentColor},rgba(${accentRgb},0.3))` }}/>

                <div style={{ padding:'0.85rem 1rem' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>

                    {/* Icon */}
                    <div style={{ position:'relative', flexShrink:0 }}>
                      <div style={{
                        width:42, height:42, borderRadius:'0.75rem',
                        background: isAct
                          ? 'linear-gradient(135deg,#075985,#0891b2)'
                          : `rgba(${accentRgb},0.1)`,
                        border:`1px solid rgba(${accentRgb},${isAct?'0.4':'0.2'})`,
                        display:'flex', alignItems:'center', justifyContent:'center',
                        boxShadow: isAct ? `0 0 14px rgba(${accentRgb},0.3)` : 'none',
                      }}>
                        <svg width="19" height="19" viewBox="0 0 24 24"
                          fill={isAct ? '#fff' : accentColor} opacity={isAct?1:0.7}>
                          <path d="M12 2C9 5.5 5 9.3 5 13.5a7 7 0 0014 0C19 9.3 15 5.5 12 2z"/>
                        </svg>
                      </div>
                      {/* Live dot */}
                      {isAct && (
                        <div style={{ position:'absolute', top:-3, left:-3 }}>
                          <div style={{
                            position:'absolute', width:10, height:10, borderRadius:'50%',
                            background:'#0ABAB5', opacity:0.5,
                            animation:'wt-live-ring 1.8s ease-out infinite',
                          }}/>
                          <div style={{
                            width:8, height:8, borderRadius:'50%',
                            background:'#0ABAB5', border:'1.5px solid var(--wt-surface)',
                            position:'relative', zIndex:1,
                            animation:'wt-live-dot 1.8s ease-in-out infinite',
                          }}/>
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.18rem' }}>
                        <span style={{
                          color:'var(--wt-text-primary)', fontWeight:700, fontSize:'0.85rem',
                          overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:'60%',
                        }}>
                          {order.order_number}
                        </span>
                        <span style={{
                          fontSize:'0.65rem', fontWeight:800, padding:'0.15rem 0.55rem', borderRadius:999,
                          background:`rgba(${accentRgb},0.12)`,
                          border:`1px solid rgba(${accentRgb},0.25)`,
                          color: accentColor, whiteSpace:'nowrap',
                        }}>
                          {st.label}
                        </span>
                      </div>
                      <p style={{ color:'var(--wt-text-muted)', fontSize:'0.7rem', margin:0, display:'flex', alignItems:'center', gap:'0.3rem' }}>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" opacity="0.6">
                          <path d="M12 2C9 5.5 5 9.3 5 13.5a7 7 0 0014 0C19 9.3 15 5.5 12 2z"/>
                        </svg>
                        {order.water_type || 'مياه'} — {order.requested_quantity || order.quantity || '—'} طن
                      </p>
                    </div>
                  </div>

                  {/* Footer */}
                  <div style={{
                    display:'flex', alignItems:'center', justifyContent:'space-between',
                    marginTop:'0.7rem', paddingTop:'0.65rem',
                    borderTop:`1px solid var(--wt-border)`,
                  }}>
                    <span style={{ color:'var(--wt-text-muted)', fontSize:'0.68rem', display:'flex', alignItems:'center', gap:'0.25rem' }}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
                      </svg>
                      {fmtDate(order.created_at)}
                    </span>
                    <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
                      {amt > 0 && (
                        <span style={{
                          color:'var(--wt-text-primary)', fontWeight:800, fontSize:'0.85rem',
                          display:'flex', alignItems:'center', gap:'0.25rem', direction:'ltr',
                        }}>
                          <SAR style={{ height:'0.9em', width:'auto', display:'inline-block', verticalAlign:'middle' }}/>
                          {amt.toFixed(2)}
                        </span>
                      )}
                      {canCancel && (
                        <button
                          onClick={e => cancelOrder(e, order.id)}
                          style={{
                            display:'flex', alignItems:'center', gap:'0.25rem',
                            padding:'0.25rem 0.55rem', borderRadius:'0.55rem',
                            background:'linear-gradient(135deg,#f43f5e,#e11d48)',
                            border:'none', cursor:'pointer',
                            color:'#fff', fontSize:'0.62rem', fontWeight:800,
                            boxShadow:'0 2px 8px rgba(244,63,94,0.35)',
                            fontFamily:"'Cairo',sans-serif",
                          }}
                        >
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round">
                            <circle cx="12" cy="12" r="9"/><path d="M15 9l-6 6M9 9l6 6"/>
                          </svg>
                          إلغاء
                        </button>
                      )}
                      <div style={{
                        width:24, height:24, borderRadius:'50%', display:'flex',
                        alignItems:'center', justifyContent:'center',
                        background:`rgba(${accentRgb},0.1)`,
                        color: accentColor,
                      }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                          <path d="M15 18l-6-6 6-6"/>
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
          {allOrders.length >= 100 && (
            <p style={{ color:'var(--wt-text-muted)', fontSize:'0.7rem', textAlign:'center', padding:'0.5rem 0' }}>
              يعرض أحدث 100 طلب
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function hexToRgb(hex) {
  const r = parseInt(hex.slice(1,3),16), g = parseInt(hex.slice(3,5),16), b = parseInt(hex.slice(5,7),16);
  return `${r},${g},${b}`;
}
